import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SwissBadge from "@/components/SwissBadge";

const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "https://www.easygutachter.de";

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(`${t("dashboard.greeting_afternoon")}, ${res.user.name}!`);
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" data-testid="login-page">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex items-center justify-between">
            <a href={WEBSITE_URL} className="inline-flex items-center gap-3 text-sm text-zinc-500 hover:text-zinc-900 transition-colors" data-testid="back-to-website">
              <ArrowLeft className="h-4 w-4" /> {t("common.back")}
            </a>
            <LanguageSwitcher />
          </div>
          <a href={WEBSITE_URL} className="flex items-center cursor-pointer hover:opacity-80 transition-opacity" data-testid="login-brand">
            <img src="/easy-gutachter-logo.png" alt="Easy Gutachter" className="h-20 w-auto select-none" draggable={false} />
          </a>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("login.title")}</h1>
            <p className="text-zinc-500 text-sm mt-2">{t("login.subtitle")}</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">{t("common.email")}</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                placeholder={t("login.email_placeholder")}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password">{t("common.password")}</Label>
                <Link to="/forgot-password" className="text-xs text-cyan-700 hover:underline" data-testid="login-forgot-link">{t("login.forgot_password")}</Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full brand-gradient-bg text-white" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {t("login.submit")}
            </Button>
          </form>
          <div className="text-sm text-center text-zinc-500">
            {t("login.no_account")}{" "}
            <Link to="/register" className="text-[#12A9E8] font-medium hover:underline" data-testid="login-to-register">
              {t("login.register_link")}
            </Link>
          </div>

          <div className="flex justify-center pt-1">
            <SwissBadge />
          </div>

        </div>
      </div>
      <div className="hidden lg:block relative brand-gradient-bg">
        <div className="absolute inset-0 grain" />
        <div className="relative h-full flex items-end p-12">
          <div className="max-w-md text-white">
            <div className="text-xs uppercase tracking-[0.22em] font-bold opacity-80">Cockpit</div>
            <div className="font-display text-4xl font-bold tracking-tight leading-[1.05] mt-2">
              Eine Akte. Ein Klick. Ein Gutachten.
            </div>
            <div className="mt-4 text-white/80">
              Über 25.000 erstellte Gutachten in 2025. Weniger Tippen, mehr Beurteilen.
            </div>
            <div className="mt-6">
              <SwissBadge variant="dark" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
